import { useState, useEffect } from 'react';
import { useFm } from '@web3/base-hooks';
import { getTokenBalance } from '@/services/wallet';
import { useAccountInfoContext } from '@/hooks/useAccountInfo';
import BigNumber from 'bignumber.js';
import { usePoolInfoContext } from '@/hooks/usePoolInfo';
import { GasStore } from '@/types/unstake';
import { useCurrentChainAddress } from '@/hooks/wallet';
import { ChainCodeTypes } from '@/types/chain';
import { getTokenAddress } from '@/config/address';
import { formatNumber } from '@web3/base-utils';

export const useBalance = () => {
  const [balance, setBalance] = useState('');
  const { poolInfo } = usePoolInfoContext();
  const { walletAddress } = useCurrentChainAddress(poolInfo.chainCode);

  const fetchBalance = async () => {
    const specialChain: string[] = [ChainCodeTypes.SUI, ChainCodeTypes.TON];

    let nativeTokenAddress = '';

    if (specialChain.includes(poolInfo.chainCode)) {
      nativeTokenAddress = getTokenAddress(poolInfo.chainCode);
    }

    const { success, result } = await getTokenBalance({
      chainId: poolInfo.chainId,
      tokenAddress: nativeTokenAddress,
      walletAddress,
    });

    if (success) {
      setBalance(result.balance);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  return {
    balance,
  };
};

export const useInputStatus = (
  balance: string,
  unstakeAmount: string,
  onChangeUnstakeAmount: (v: string) => void,
  onChangeTextIsError: (v: boolean) => void,
  gasStore: GasStore
) => {
  const { accountInfo } = useAccountInfoContext();
  const { poolInfo } = usePoolInfoContext();
  const fm = useFm();

  const isShowMax = BigNumber(balance).gt(0);

  const { min, max } = poolInfo?.poolExtendDTO?.unstakeRange || {};

  const inputFontSize = (() => {
    if (unstakeAmount?.length < 15) {
      return 32;
    }

    return 24;
  })();

  const onMax = () => {
    onChangeUnstakeAmount(
      formatNumber(accountInfo.lpTokens, {
        kmb: true,
        decimal: 8,
        smallNumberUseValidDigits: false,
        collapseZero: false,
      })
    );
  };

  const getInputErrorText = () => {
    if (min && BigNumber(unstakeAmount).lt(min)) {
      onChangeTextIsError(true);
      return fm('minRedemption', {
        num: min,
        token_name: 'LP',
      });
    }

    if (max && BigNumber(unstakeAmount).gt(max)) {
      onChangeTextIsError(true);
      return fm('createMaxInvestment', {
        amount: max,
      });
    }

    if (BigNumber(unstakeAmount).gt(accountInfo.lpTokens)) {
      onChangeTextIsError(true);
      return fm('token_balance_due', { token: 'LP' });
    }

    if (BigNumber(gasStore?.amount || 0).gt(balance)) {
      onChangeTextIsError(true);
      return fm('gas_fee_less');
    }

    onChangeTextIsError(false);
    return '';
  };

  const inputErrorText = getInputErrorText();

  return {
    inputFontSize,
    inputErrorText,
    isShowMax,
    onMax,
  };
};
